import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const checkboxVariants = cva(
	'peer shrink-0 shadow focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 transition duration-200 ease-in-out transform data-[state=checked]:scale-110 data-[state=unchecked]:scale-95',
	{
		variants: {
			rounded: {
				default: 'rounded-sm',
				full: 'rounded-full',
			},
			size: {
				default: 'h-4 w-4',
				sm: 'h-3 w-3',
				md: 'h-4 w-4',
				lg: 'h-5 w-5',
				xl: 'h-7 w-7',
			},
			variant: {
				default: 'border border-primary',
				outline: 'border-2 border-dashed border-primary',
				custom: 'border border-primary bg-gradient-to-r from-purple-400 to-blue-500',
				blue: 'border border-blue-600 text-blue-600 data-[state=checked]:bg-blue-600 data-[state=checked]:text-white',
			},
		},
		defaultVariants: {
			rounded: 'default',
			size: 'default',
			variant: 'default',
		},
	},
);

interface CheckboxProps
	extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
		VariantProps<typeof checkboxVariants> {}

const Checkbox = React.forwardRef<React.ElementRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
	({ className, rounded, size, variant, ...props }, ref) => (
		<CheckboxPrimitive.Root
			ref={ref}
			className={cn(checkboxVariants({ rounded, size, variant }), className)}
			{...props}>
			<CheckboxPrimitive.Indicator
				className={cn(
					'flex items-center justify-center text-current transition-opacity duration-200 ease-in-out',
					'data-[state=checked]:opacity-100 data-[state=unchecked]:opacity-0',
				)}>
				<Check className="h-4 w-4" />
			</CheckboxPrimitive.Indicator>
		</CheckboxPrimitive.Root>
	),
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
